import React from 'react';
import { connect } from 'react-redux';
import {
  Button,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideDrawer } from '../../../theme/components/GuideDrawer';
import {
  ArrowDownload20Regular,
  Dismiss24Regular,
  DocumentDismiss24Regular,
  FullScreenMaximize20Regular,
  FullScreenMinimize20Regular,
  Open20Regular,
  ZoomIn20Regular,
  ZoomOut20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as ComponentFunctions from './FilePreview.Functions';
import { useFilePreviewStyles } from './FilePreview.Styles';
import { formatFileSize } from '../../../functions/AzureFileUpload.Functions';
import {
  Translate,
  buildFriendlyLocation,
  collectStorageEntityRefs,
} from '../../../functions/StorageBrowser.Functions';
import { closePendingTab, openPendingTab, showInTab, triggerDownload } from '../FilesExplorer/FilesExplorer.Functions';
import { ErrorType } from '../../../models/HttpError';

type LoadState<T> = { status: 'idle' | 'loading' | 'ready' | 'error'; value?: T; error?: ErrorType };

const IDLE = { status: 'idle' } as const;

/**
 * Wide guide drawer (GuideDrawer size "wide": min(1100px, 75vw), full width under 768px; "Expandir" switches to
 * "full" and the choice is remembered for the session) that previews a storage file (pdf, images, audio/video, text, csv and Office
 * documents through Office Online). The read SAS (15 min) is requested every time it opens and
 * on "Reintentar", always through the middleware.
 */
export const FilePreviewComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const translate = t as unknown as Translate;
  const styles = useFilePreviewStyles();

  const { open, file, RequestPreviewSas, RequestContractFileSas, FetchPreviewText, ResolveEntityNames } = props;

  const [attempt, setAttempt] = React.useState(0);
  const [sas, setSas] = React.useState<LoadState<string>>(IDLE);
  const [text, setText] = React.useState<LoadState<string>>(IDLE);
  const [zoomed, setZoomed] = React.useState(false);
  const [busyAction, setBusyAction] = React.useState<'download' | 'open' | undefined>(undefined);
  const [actionError, setActionError] = React.useState<ErrorType | undefined>(undefined);
  const [expanded, setExpanded] = React.useState<boolean>(() => ComponentFunctions.readPreviewExpanded());

  const toggleExpanded = () => {
    setExpanded((current) => {
      ComponentFunctions.writePreviewExpanded(!current);
      return !current;
    });
  };

  const extension = file ? ComponentFunctions.getPreviewExtension(file) : '';
  const decision = React.useMemo(
    () => ComponentFunctions.getPreviewDecision(extension, file?.size),
    [extension, file?.size]
  );

  React.useEffect(() => {
    setZoomed(false);
    setActionError(undefined);
    setText(IDLE);
    if (!open || !file) {
      setSas(IDLE);
      return;
    }
    ResolveEntityNames(collectStorageEntityRefs([file.path]));
    if (!ComponentFunctions.needsSas(decision)) {
      setSas(IDLE);
      return;
    }

    let cancelled = false;
    setSas({ status: 'loading' });
    (file.contractFileId ? RequestContractFileSas(file.contractFileId, false) : RequestPreviewSas(file.path, false))
      .then((token) => {
        if (cancelled) return;
        setSas({ status: 'ready', value: token.sasUrl });
        if (ComponentFunctions.needsText(decision)) {
          setText({ status: 'loading' });
          FetchPreviewText(token.sasUrl, decision.limitBytes ?? ComponentFunctions.PREVIEW_TEXT_MAX_BYTES)
            .then((content) => {
              if (!cancelled) setText({ status: 'ready', value: content });
            })
            .catch((error: ErrorType) => {
              if (!cancelled) setText({ status: 'error', error });
            });
        }
      })
      .catch((error: ErrorType) => {
        if (!cancelled) {
          setSas({ status: 'error', error });
          props.onFileError?.(file.path, error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, file?.path, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const csv = React.useMemo(
    () =>
      decision.renderer === 'csv' && text.status === 'ready' ? ComponentFunctions.parseCsv(text.value ?? '') : undefined,
    [decision.renderer, text.status, text.value]
  );

  if (!file) return null;

  const friendlyName = ComponentFunctions.getFriendlyFileName(file.name);
  const location = buildFriendlyLocation(file.path, translate, props.ownerNames);
  const retry = () => setAttempt((value) => value + 1);
  /** Contract attachments ask the SAS by id; storage files by path. */
  const requestSas = (download: boolean) =>
    file.contractFileId ? RequestContractFileSas(file.contractFileId, download) : RequestPreviewSas(file.path, download);

  const download = async () => {
    if (busyAction) return;
    setBusyAction('download');
    setActionError(undefined);
    try {
      const token = await requestSas(true);
      triggerDownload(token.sasUrl, file.name);
    } catch (error: unknown) {
      setActionError(error as ErrorType);
      props.onFileError?.(file.path, error as ErrorType);
    } finally {
      setBusyAction(undefined);
    }
  };

  const openInNewTab = async () => {
    if (busyAction) return;
    // The tab is opened inside the click so the popup blocker lets it through.
    const tab = openPendingTab();
    setBusyAction('open');
    setActionError(undefined);
    try {
      const token = await requestSas(false);
      showInTab(tab, token.sasUrl);
    } catch (error: unknown) {
      closePendingTab(tab);
      setActionError(error as ErrorType);
    } finally {
      setBusyAction(undefined);
    }
  };

  //#region render helpers

  const renderError = (error: ErrorType | undefined, onRetry: () => void, testId: string) => {
    const detail = ComponentFunctions.getPreviewErrorDetail(error);
    return (
      <GuideMessageBar intent="error" layout="multiline" data-testid={testId}>
        <MessageBarBody>
          <MessageBarTitle>{t(ComponentFunctions.getPreviewErrorTitleKey(error))}</MessageBarTitle>
          {detail || t('filePreview.errors.genericHelp')}
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="transparent" size="small" onClick={onRetry} data-testid="file-preview-retry">
            {t('filePreview.actions.retry')}
          </Button>
        </MessageBarActions>
      </GuideMessageBar>
    );
  };

  const renderSkeleton = () => (
    <Skeleton aria-label={t('filePreview.loading')} className={styles.skeleton} data-testid="file-preview-skeleton">
      <SkeletonItem size={24} />
      <SkeletonItem shape="rectangle" className={styles.skeletonBlock} />
      <SkeletonItem size={16} />
    </Skeleton>
  );

  const renderDownloadState = (titleKey: string, helpKey: string, options?: Record<string, unknown>, testId?: string) => (
    <div className={styles.emptyState} data-testid={testId ?? 'file-preview-unsupported'}>
      <DocumentDismiss24Regular className={styles.emptyIcon} aria-hidden />
      <span className={styles.emptyTitle}>{t(titleKey, options)}</span>
      <span className={styles.emptyText}>{t(helpKey, options)}</span>
      <Button
        appearance="primary"
        icon={busyAction === 'download' ? <Spinner size="extra-tiny" /> : <ArrowDownload20Regular />}
        onClick={download}
        disabled={!!busyAction}
      >
        {t('filePreview.actions.download')}
      </Button>
    </div>
  );

  const renderText = () => {
    if (text.status === 'error') {
      if (text.error?.ErrorCode === 413) {
        return renderDownloadState('filePreview.tooLarge.title', 'filePreview.tooLarge.text', {
          size: formatFileSize(decision.limitBytes),
        }, 'file-preview-too-large');
      }
      if (text.error?.ErrorCode === 0) {
        return (
          <GuideMessageBar intent="warning" layout="multiline" data-testid="file-preview-cors">
            <MessageBarBody>
              <MessageBarTitle>{t('filePreview.errors.readTitle')}</MessageBarTitle>
              {t('filePreview.errors.cors')}
            </MessageBarBody>
            <MessageBarActions>
              <Button appearance="transparent" size="small" onClick={download} disabled={!!busyAction}>
                {t('filePreview.actions.download')}
              </Button>
              <Button appearance="transparent" size="small" onClick={retry}>
                {t('filePreview.actions.retry')}
              </Button>
            </MessageBarActions>
          </GuideMessageBar>
        );
      }
      return renderError(text.error, retry, 'file-preview-text-error');
    }
    if (text.status !== 'ready') return renderSkeleton();

    if (decision.renderer === 'csv' && csv) {
      const columns = Array.from({ length: csv.columnCount }, (_, index) => index);
      return (
        <>
          {csv.truncated && (
            <GuideMessageBar intent="info" data-testid="file-preview-csv-truncated">
              <MessageBarBody>{t('filePreview.csv.truncated', { rows: csv.rows.length })}</MessageBarBody>
            </GuideMessageBar>
          )}
          <div className={styles.tableContainer}>
            <Table size="small" aria-label={t('filePreview.csv.tableLabel', { name: friendlyName })} data-testid="file-preview-csv">
              <TableHeader>
                <TableRow>
                  {columns.map((index) => (
                    <TableHeaderCell key={index} className={styles.headerCell}>
                      {csv.header[index] ?? ''}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {csv.rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((index) => (
                      <TableCell key={index} className={styles.cell}>
                        {row[index] ?? ''}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      );
    }
    return (
      <pre className={styles.text} tabIndex={0} aria-label={friendlyName} data-testid="file-preview-text">
        {text.value}
      </pre>
    );
  };

  const renderContent = () => {
    if (decision.blockedReason === 'officeTooLarge') {
      return renderDownloadState('filePreview.office.tooLargeTitle', 'filePreview.office.tooLargeText', {
        size: formatFileSize(decision.limitBytes),
      }, 'file-preview-office-too-large');
    }
    if (decision.blockedReason === 'textTooLarge') {
      return renderDownloadState('filePreview.tooLarge.title', 'filePreview.tooLarge.text', {
        size: formatFileSize(decision.limitBytes),
      }, 'file-preview-too-large');
    }
    if (decision.renderer === 'unsupported') {
      return renderDownloadState('filePreview.unsupported.title', 'filePreview.unsupported.text', {
        extension: extension ? extension.toUpperCase() : '—',
      });
    }
    if (sas.status === 'error') return renderError(sas.error, retry, 'file-preview-error');
    if (sas.status !== 'ready' || !sas.value) return renderSkeleton();

    const url = sas.value;
    switch (decision.renderer) {
      case 'pdf':
        return (
          <iframe
            className={styles.frame}
            src={ComponentFunctions.buildPdfViewerUrl(url)}
            title={t('filePreview.frameTitle', { name: friendlyName })}
            referrerPolicy="no-referrer"
            data-testid="file-preview-pdf"
          />
        );
      case 'image':
        return (
          <>
            <div className={styles.toolbar}>
              <Button
                appearance="subtle"
                icon={zoomed ? <ZoomOut20Regular /> : <ZoomIn20Regular />}
                aria-pressed={zoomed}
                onClick={() => setZoomed((value) => !value)}
                data-testid="file-preview-zoom"
              >
                {t(zoomed ? 'filePreview.actions.fit' : 'filePreview.actions.zoom')}
              </Button>
            </div>
            <div className={mergeClasses(styles.imageContainer, zoomed && styles.imageContainerZoomed)}>
              <img
                className={mergeClasses(styles.imageFit, zoomed && styles.imageZoomed)}
                src={url}
                alt={friendlyName}
                referrerPolicy="no-referrer"
                onClick={() => setZoomed((value) => !value)}
                data-testid="file-preview-image"
              />
            </div>
          </>
        );
      case 'video':
        return (
          <video className={styles.media} src={url} controls preload="metadata" data-testid="file-preview-video">
            {t('filePreview.mediaUnsupported')}
          </video>
        );
      case 'audio':
        return (
          <audio src={url} controls preload="metadata" data-testid="file-preview-audio">
            {t('filePreview.mediaUnsupported')}
          </audio>
        );
      case 'office':
        return (
          <>
            <GuideMessageBar intent="info" layout="multiline" data-testid="file-preview-office-notice">
              <MessageBarBody>
                <MessageBarTitle>{t('filePreview.office.noticeTitle')}</MessageBarTitle>
                {t('filePreview.office.notice')} {t('filePreview.readOnly')}
              </MessageBarBody>
            </GuideMessageBar>
            <iframe
              className={styles.frame}
              src={ComponentFunctions.buildOfficeViewerUrl(url, decision.officeApp)}
              title={t('filePreview.frameTitle', { name: friendlyName })}
              referrerPolicy="no-referrer"
              sandbox={ComponentFunctions.OFFICE_VIEWER_SANDBOX}
              data-testid="file-preview-office"
            />
          </>
        );
      case 'text':
      case 'csv':
        return renderText();
      default:
        return null;
    }
  };

  //#endregion

  const meta = [
    file.size !== undefined && file.size !== null ? formatFileSize(file.size) : undefined,
    file.lastModified ? moment(file.lastModified).format('DD/MM/YYYY HH:mm') : undefined,
  ].filter(Boolean);

  return (
    <GuideDrawer
      open={open}
      size={ComponentFunctions.getPreviewDrawerSize(expanded)}
      ariaLabel={t('filePreview.ariaLabel', { name: friendlyName })}
      onDismiss={props.onDismiss}
      closeLabel={t('filePreview.actions.close')}
      testId="file-preview"
      bodyClassName={styles.drawerBody}
      secondaryAction={false}
      eyebrow={
        location ? (
          <span className={styles.location} title={file.path} data-testid="file-preview-location">
            {location}
          </span>
        ) : undefined
      }
      title={
        <Tooltip content={file.name} relationship="description" withArrow>
          <span className={styles.fileName} tabIndex={0} data-testid="file-preview-name">
            {friendlyName}
          </span>
        </Tooltip>
      }
      subtitle={
        <span className={styles.meta} data-testid="file-preview-meta">
          {meta.length > 0 && <span>{meta.join(' · ')} · </span>}
          <span title={t('filePreview.readOnly')} data-testid="file-preview-read-only">
            {t('filePreview.readOnlyShort')}
          </span>
        </span>
      }
      headerActions={
        <>
          <Button
            appearance="subtle"
            icon={expanded ? <FullScreenMinimize20Regular /> : <FullScreenMaximize20Regular />}
            onClick={toggleExpanded}
            aria-pressed={expanded}
            title={expanded ? t('filePreview.actions.collapseHelp') : t('filePreview.actions.expandHelp')}
            data-testid="file-preview-expand"
          >
            {expanded ? t('filePreview.actions.collapse') : t('filePreview.actions.expand')}
          </Button>
          <Button
            appearance="subtle"
            icon={busyAction === 'download' ? <Spinner size="extra-tiny" /> : <ArrowDownload20Regular />}
            onClick={download}
            disabled={!!busyAction}
            data-testid="file-preview-download"
          >
            {t('filePreview.actions.download')}
          </Button>
          <Button
            appearance="subtle"
            icon={busyAction === 'open' ? <Spinner size="extra-tiny" /> : <Open20Regular />}
            onClick={openInNewTab}
            disabled={!!busyAction}
            data-testid="file-preview-open-tab"
          >
            {t('filePreview.actions.openInNewTab')}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        {actionError && (
          <GuideMessageBar intent="error" layout="multiline" data-testid="file-preview-action-error">
            <MessageBarBody>
              <MessageBarTitle>{t(ComponentFunctions.getPreviewErrorTitleKey(actionError))}</MessageBarTitle>
              {ComponentFunctions.getPreviewErrorDetail(actionError) || t('filePreview.errors.genericHelp')}
            </MessageBarBody>
            <MessageBarActions
              containerAction={
                <Button
                  appearance="transparent"
                  size="small"
                  icon={<Dismiss24Regular />}
                  aria-label={t('filePreview.actions.dismiss')}
                  onClick={() => setActionError(undefined)}
                />
              }
            />
          </GuideMessageBar>
        )}
        {renderContent()}
      </div>
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(FilePreviewComponent as React.ComponentType<ComponentFunctions.Props>);
